import React, {useCallback, useEffect, useState} from "react";
import {Button, Col, Input, Modal, Row, Space, Table, Typography} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {CheckCircleOutlined, SearchOutlined, UndoOutlined} from "@ant-design/icons";
import theme from "../theme";
import affiliateRollCallService from "../services/AffiliateRollCallService";
import {useSelector} from "react-redux";
import formatDate from "../utils/dateUtils";
import {COT_ROLE, DISTRICT_ROLE, PROMOTER_ROLE, SECTIONAL_ROLE, STATE_ROLE, useUserRole} from "../utils/userSessionUtils";

const {Title, Text} = Typography;

const RESTRICTED_GROUPS = ["refuerzo", "brigadista", "brigadistas"];

const PaseDeListaAfiliadosPage = () => {
    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const userRoute = auth.user?.route || [];
    const userGroup = auth.user?.group || "";
    const userRole = useUserRole();

    const isRestricted = userRole === COT_ROLE
        && RESTRICTED_GROUPS.includes(userGroup.toLowerCase());

    const filtersByCreatedBy = [PROMOTER_ROLE, SECTIONAL_ROLE].includes(userRole)
        || isRestricted
        || (userRole === DISTRICT_ROLE && userGroup.toLowerCase() !== "estructura");

    const filtersByDistrict = userRole === DISTRICT_ROLE && userGroup.toLowerCase() === "estructura";

    const filtersByState = userRole === STATE_ROLE && userGroup.toLowerCase() === "estructura";

    const useSimpleColumns = isRestricted || [PROMOTER_ROLE, SECTIONAL_ROLE].includes(userRole);

    const [affiliates, setAffiliates] = useState([]);
    const [rollCallLoading, setRollCallLoading] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});
    const [tempFilters, setTempFilters] = useState({
        nombre: "", paterno: "", materno: "", telefono: "",
        seccion: userRoute,
    });
    const [appliedFilters, setAppliedFilters] = useState({});
    const [modal, contextHolder] = Modal.useModal();

    const fetchAffiliates = useCallback(async (page, pageSize, filters) => {
        setLoading(true);
        try {
            const response = await affiliateRollCallService.getAffiliates(page, pageSize, filters);
            setAffiliates(response.data);
            setPagination((prev) => ({...prev, current: page, pageSize, total: response.meta.totalItems}));
        } catch (error) {
            console.error("Error al cargar afiliados:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAffiliates(1, 10, {}).catch(console.error);
    }, [fetchAffiliates]);

    const handleTableChange = (newPagination) => {
        fetchAffiliates(newPagination.current, newPagination.pageSize, appliedFilters).catch(console.error);
    };

    const handleFilterChange = (key, value) => {
        setTempFilters((prev) => ({...prev, [key]: value}));
    };

    const applyFilters = () => {
        const filters = {
            nombre: tempFilters.nombre,
            paterno: tempFilters.paterno,
            materno: tempFilters.materno,
            telefono: tempFilters.telefono,
        };
        setAppliedFilters(filters);
        fetchAffiliates(1, pagination.pageSize, filters).catch(console.error);
    };

    const resetFilters = () => {
        setTempFilters({nombre: "", paterno: "", materno: "", telefono: "", seccion: userRoute});
        setAppliedFilters({});
        fetchAffiliates(1, pagination.pageSize, {}).catch(console.error);
    };

    const formatAddress = (record) => {
        const parts = [
            `${record.calle || ""}${record.numero ? ` ${record.numero}` : ""}${record.numeroInt ? ` Int. ${record.numeroInt}` : ""}`,
            record.colonia,
            record.cp ? `CP ${record.cp}` : "",
        ].filter(Boolean);
        return parts.join(", ") || "—";
    };

    const handleRollCallClick = (record) => {
        const fullName = `${record.nombre} ${record.paterno} ${record.materno || ""}`.trim();
        modal.confirm({
            title: "Confirmar Pase de Lista",
            content: (
                <div>
                    <p>¿Desea marcar asistencia para?</p>
                    <p><strong>{fullName}</strong></p>
                    <p>Sección: <strong>{record.seccion}</strong></p>
                </div>
            ),
            okText: "Sí, marcar asistencia",
            cancelText: "Cancelar",
            okType: "primary",
            onOk: async () => {
                await handleMarkAttendance(record);
            },
        });
    };

    const handleMarkAttendance = async (record) => {
        const recordId = record.claveElector;
        setRollCallLoading((prev) => ({...prev, [recordId]: true}));
        try {
            await affiliateRollCallService.create({
                claveElector: record.claveElector,
                entidad: record.entidad,
                seccion: record.seccion,
                distritoLocal: record.distritoLocal,
            });
            const fullName = `${record.nombre} ${record.paterno} ${record.materno || ""}`.trim();
            modal.success({
                title: "Asistencia marcada exitosamente",
                content: (
                    <div>
                        <p><strong>{fullName}</strong></p>
                        <p>Sección: <strong>{record.seccion}</strong></p>
                    </div>
                ),
            });
            setAffiliates((prev) =>
                prev.map((a) =>
                    a.claveElector === record.claveElector
                        ? {...a, attendanceAt: new Date().toISOString()}
                        : a
                )
            );
        } catch (error) {
            console.error("Error al marcar asistencia:", error);
            let msg = "Error inesperado al marcar asistencia";
            if (error.details && typeof error.details === "string") msg = error.details;
            else if (error.details && Array.isArray(error.details)) msg = error.details.join(", ");
            modal.error({title: "Error al marcar asistencia.", content: msg});
        } finally {
            setRollCallLoading((prev) => ({...prev, [recordId]: false}));
        }
    };

    const actionColumn = {
        title: "Acciones",
        key: "actions",
        render: (record) => (
            <Button
                type={record.attendanceAt ? "default" : "primary"}
                icon={<CheckCircleOutlined/>}
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleRollCallClick(record);
                }}
                loading={!!rollCallLoading[record.claveElector]}
                disabled={!!record.attendanceAt}
            >
                {record.attendanceAt ? "Marcado" : "Pase de Lista"}
            </Button>
        ),
    };

    const limitedColumns = [
        actionColumn,
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => `${record.nombre} ${record.paterno} ${record.materno || ""}`.trim(),
        },
        {title: "Teléfono", dataIndex: "telefono", key: "telefono", render: (v) => v || "—"},
        {title: "Dirección", key: "address", render: formatAddress},
    ];

    const fullColumns = [
        actionColumn,
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => `${record.nombre} ${record.paterno} ${record.materno || ""}`.trim(),
        },
        {title: "Sección", dataIndex: "seccion", key: "seccion"},
        {title: "Teléfono", dataIndex: "telefono", key: "telefono", render: (v) => v || "—"},
        {title: "Dirección", key: "address", render: formatAddress},
        {
            title: "Pase de lista por",
            dataIndex: "attendanceBy",
            key: "attendanceBy",
            render: (val) => val || "—",
        },
        {
            title: "Fecha de asistencia",
            dataIndex: "attendanceAt",
            key: "attendanceAt",
            render: (val) => (val ? formatDate(val) : "—"),
        },
    ];

    const mobileColumns = [
        {
            title: "Afiliado",
            key: "info",
            render: (record) => (
                <div>
                    <strong>{`${record.nombre} ${record.paterno}`}</strong>
                    <div style={{fontSize: "12px", color: "#666"}}>{record.telefono || "Sin teléfono"}</div>
                    <div style={{fontSize: "12px", color: "#666"}}>{formatAddress(record)}</div>
                    {record.attendanceBy && (
                        <div style={{fontSize: "12px", color: "#666"}}>Lista por: {record.attendanceBy}</div>
                    )}
                </div>
            ),
        },
        {
            title: "Acción",
            key: "actions",
            render: (record) => (
                <Button
                    type={record.attendanceAt ? "default" : "primary"}
                    icon={<CheckCircleOutlined/>}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRollCallClick(record);
                    }}
                    loading={!!rollCallLoading[record.claveElector]}
                    disabled={!!record.attendanceAt}
                    block
                >
                    {record.attendanceAt ? "Marcado" : "Pase de Lista"}
                </Button>
            ),
        },
    ];

    const isAvailable = new Date() >= new Date("2026-06-07T00:00:00");

    if (true) {
        return (
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: "500px",
                    margin: "60px auto",
                    background: theme.colors.card,
                    padding: "40px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    textAlign: "center",
                }}
            >
                <Title level={3}>Pase de Lista — Afiliados</Title>
                <p style={{fontSize: "16px", color: "#666"}}>
                    El pase de lista para esta jornada ha concluido
                </p>
            </motion.div>
        );
    }

    return (
        <>
            {contextHolder}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <Title level={2} style={{textAlign: "center"}}>Pase de Lista — Afiliados</Title>

                {!filtersByCreatedBy && !isRestricted && (
                    <div style={{
                        marginBottom: "16px",
                        padding: "8px 12px",
                        background: "rgba(178, 139, 71, 0.12)",
                        border: "1px solid rgba(178, 139, 71, 0.4)",
                        borderRadius: "6px",
                        fontSize: "13px",
                    }}>
                        Realiza la búsqueda SIN acentos.
                    </div>
                )}

                <Row gutter={[16, 16]} style={{marginBottom: "16px"}}>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Nombre"
                            value={tempFilters.nombre}
                            onChange={(e) => handleFilterChange("nombre", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Paterno"
                            value={tempFilters.paterno}
                            onChange={(e) => handleFilterChange("paterno", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Materno"
                            value={tempFilters.materno}
                            onChange={(e) => handleFilterChange("materno", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Teléfono"
                            value={tempFilters.telefono}
                            onChange={(e) => handleFilterChange("telefono", e.target.value)}
                        />
                    </Col>
                    {!filtersByCreatedBy && !isRestricted && (
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Sección (número)"
                                value={tempFilters.seccion.join(",")}
                                onChange={(e) => {
                                    const vals = e.target.value
                                        .split(",")
                                        .map((v) => v.trim())
                                        .filter((v) => v !== "" && !isNaN(v))
                                        .map(Number);
                                    handleFilterChange("seccion", vals);
                                }}
                            />
                        </Col>
                    )}
                </Row>

                <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}}>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined/>} onClick={applyFilters}>
                            Aplicar Filtros
                        </Button>
                        <Button icon={<UndoOutlined/>} onClick={resetFilters}>
                            Restablecer
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={isMobile ? mobileColumns : (useSimpleColumns ? limitedColumns : fullColumns)}
                    dataSource={affiliates}
                    rowKey="claveElector"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{x: "max-content"}}
                />

                <Text style={{textAlign: "right", display: "block", marginTop: "10px"}}>
                    Mostrando {affiliates.length} de {pagination.total} registros
                </Text>
            </motion.div>
        </>
    );
};

export default PaseDeListaAfiliadosPage;
